import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import useProducts from '../../hooks/useProducts';
import {
  selectInStock,
  selectOnSale,
  selectPriceRange,
  selectSelectedCategories,
  selectSelectedColors,
  selectSortOrder,
} from '../../redux/selectors/productSelector';
import {
  resetCategories,
  resetColors,
  resetFilters,
  setInStock,
  setOnSale,
  setPriceRange,
  setSelectedCategories,
  setSelectedColor,
  setSortOrder,
} from '../../redux/slices/productSlice';
import { filterAndSortProducts } from '../../utils/filtering';
import { addItemToCart } from '../../redux/slices/cartSlice';
import { toggleWishlistItem } from '../../redux/slices/wishlistSlice';
import { toggleCompareItem } from '../../redux/slices/compareSlice';
import { sortProducts } from '../../utils/sorting';
import Breadcrumb from '../../components/Breadcrumb';
import SingleShop from '../../components/Shop';
import Button from '../../components/Button';

import brandImg1 from '../../assets/img/shop/brand/brand-1.png';
import brandImg2 from '../../assets/img/shop/brand/brand-2.png';
import brandImg3 from '../../assets/img/shop/brand/brand-3.png';
import brandImg4 from '../../assets/img/shop/brand/brand-4.png';
import brandImg5 from '../../assets/img/shop/brand/brand-5.png';
import brandImg6 from '../../assets/img/shop/brand/brand-6.png';
import brandImg7 from '../../assets/img/shop/brand/brand-7.png';
import brandImg8 from '../../assets/img/shop/brand/brand-8.png';

const ShopSidebarMain = () => {
  const { products, status, error } = useProducts(); // Use the custom hook to fetch product data and manage loading/error states
  const [activeCategory, setActiveCategory] = useState(null);
  const [activeColor, setActiveColor] = useState(null);
  const [displayProducts, setDisplayProducts] = useState([]);
  const dispatch = useDispatch();

  // Selectors to get state from Redux
  const selectedSort = useSelector(selectSortOrder);
  const priceRange = useSelector(selectPriceRange);
  const selectedCategories = useSelector(selectSelectedCategories);
  const selectedColors = useSelector(selectSelectedColors);
  const onSale = useSelector(selectOnSale);
  const inStock = useSelector(selectInStock);

  // Dispatch the action to display products by price range slider
  const handlePriceRangeChange = (range) => {
    dispatch(setPriceRange(range));
  };

  // Dispatch the action to display on sale products
  const handleOnSaleChange = () => {
    dispatch(setOnSale(!onSale));
    dispatch(setInStock(false));
  };

  // Dispatch the action to display in stock products
  const handleInStockChange = () => {
    dispatch(setInStock(!inStock));
    dispatch(setOnSale(false));
  };

  // Dispatch the action to display all categories products
  const handleAllCategories = () => {
    dispatch(resetCategories());
    setActiveCategory('all-categories');
  };

  // Dispatch the action to display specific category products
  const handleCategoryChange = (category) => {
    dispatch(setSelectedCategories(category));
    setActiveCategory(category);
  };

  // Dispatch the action to display all categories products
  const handleAllColors = () => {
    dispatch(resetColors());
    setActiveColor('all-colors');
  };

  // Dispatch the action to display based on color
  const handleColorChange = (color) => {
    dispatch(setSelectedColor(color));
    setActiveColor(color);
  };

  // Dispatch the action to display product with selected sort value
  const handleSortCriteriaChange = (e) => {
    dispatch(setSortOrder(e.target.value));
  };

  // Reset price range slider
  const handleReset = () => {
    dispatch(setPriceRange([80, 700]));
  };

  // Filtering and sorting products whenever the sort order or filter change
  useEffect(() => {
    const filteredProducts = filterAndSortProducts(
      products,
      priceRange,
      selectedCategories,
      selectedColors,
      onSale,
      inStock
    );

    const sortedProducts = sortProducts(filteredProducts, selectedSort);
    setDisplayProducts(sortedProducts);
  }, [
    products,
    priceRange,
    selectedCategories,
    selectedColors,
    onSale,
    inStock,
    selectedSort,
  ]);

  // Reset filters ans sort
  useEffect(() => {
    dispatch(resetFilters());
  }, []);

  // Dispatch the action to add the item to cart
  const handleAddToCart = (product) => {
    dispatch(
      addItemToCart({
        product: product,
      })
    );
  };

  // Dispatch the action to toggle the item to the wishlist
  const handleToggleWishlist = (product) => {
    dispatch(toggleWishlistItem(product));
  };

  // Dispatch the action to toggle the item to the Compare
  const handleToggleCompare = (product) => {
    dispatch(toggleCompareItem(product));
  };

  // Handler for continue shopping when u have no product to display
  const handleShopping = () => {
    dispatch(resetFilters());
    setActiveCategory(null);
    setActiveColor(null);
  };

  // All Categories
  const categories = [
    'Thermometer',
    'Face Mask',
    'Pathology Products',
    'Drugs',
    'Hospital Equipment',
    'Daily Basis',
  ];

  // All Color
  const colors = ['Red', 'Dark Blue', 'Orange', 'Purple', 'Yellow', 'Green'];

  // Render a loading message while the products are being fetched
  if (status === 'loading') {
    return <div>Loading...</div>;
  }

  // Render an error message if the fetch request fails
  if (status === 'failed') {
    return <div>Error: {error}</div>;
  }

  return (
    <main>
      <Breadcrumb title="Shop Sidebar" subTitle="Shop" />

      <div className="it-shop-area pt-120 pb-120">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4">
              <div className="it-shop-sidebar mr-10">
                <div className="it-shop-widget mb-35">
                  <h3 className="it-shop-widget-title no-border">
                    Price Filter
                  </h3>
                  <div className="it-shop-widget-content">
                    <div className="it-shop-widget-filter">
                      <div id="slider-range" className="mb-10">
                        <Slider
                          range
                          min={0}
                          max={1000}
                          defaultValue={priceRange}
                          value={priceRange}
                          onChange={handlePriceRangeChange}
                          trackStyle={[{ backgroundColor: '#000' }]}
                          handleStyle={[
                            { borderColor: '#000' },
                            { borderColor: '#000' },
                          ]}
                        />
                      </div>
                      <div className="it-shop-widget-filter-info d-flex align-items-center justify-content-between">
                        <span className="input-range text-black">
                          ${priceRange[0]} - ${priceRange[1]}
                        </span>
                        <button
                          className="it-shop-widget-filter-btn"
                          type="button"
                          onClick={() => handleReset()}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="it-shop-widget mb-50">
                  <h3 className="it-shop-widget-title">Product Status</h3>

                  <div className="it-shop-widget-content">
                    <div className="it-shop-widget-checkbox">
                      <ul className="filter-items filter-checkbox">
                        <li className="filter-item checkbox">
                          <input
                            id="on_sale"
                            type="checkbox"
                            checked={onSale}
                            onChange={handleOnSaleChange}
                          />
                          <label htmlFor="on_sale">On sale</label>
                        </li>
                        <li className="filter-item checkbox">
                          <input
                            id="in_stock"
                            type="checkbox"
                            checked={inStock}
                            onChange={handleInStockChange}
                          />
                          <label htmlFor="in_stock">In Stock</label>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="it-shop-widget mb-50">
                  <h3 className="it-shop-widget-title">Categories</h3>

                  <div className="it-shop-widget-content">
                    <div className="it-shop-widget-categories">
                      <ul>
                        <li>
                          <Link
                            to="#"
                            onClick={() => handleAllCategories()}
                            className={
                              activeCategory === 'all-categories'
                                ? 'active'
                                : ''
                            }
                          >
                            All Categories <span>{displayProducts.length}</span>
                          </Link>
                        </li>
                        {categories.map((category, index) => (
                          <li key={index}>
                            <Link
                              to="#"
                              onClick={() => handleCategoryChange(category)}
                              className={
                                activeCategory === category ? 'active' : ''
                              }
                            >
                              {category}
                              <span>
                                {
                                  displayProducts.filter(
                                    (product) => product.category === category
                                  ).length
                                }
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="it-shop-widget mb-50">
                  <h3 className="it-shop-widget-title">Filter by Color</h3>
                  <div className="it-shop-widget-content">
                    <div className="it-shop-widget-checkbox-circle-list">
                      <ul>
                        <li>
                          <div className="it-shop-widget-checkbox-circle">
                            <input
                              type="checkbox"
                              id="all-colors"
                              checked={selectedColors.length === 0}
                              onChange={() => handleAllColors()}
                            />
                            <label
                              htmlFor="all-colors"
                              className={
                                activeColor === 'all-colors'
                                  ? 'color-active'
                                  : ''
                              }
                            >
                              All colors
                            </label>
                            <span className="it-shop-widget-checkbox-circle-self all-color"></span>
                          </div>
                          <span
                            className={
                              activeColor === 'all-colors'
                                ? 'it-shop-widget-checkbox-circle-number color-active'
                                : 'it-shop-widget-checkbox-circle-number'
                            }
                          >
                            {displayProducts.length}
                          </span>
                        </li>
                        {colors.map((color) => (
                          <li key={color}>
                            <div className="it-shop-widget-checkbox-circle">
                              <input
                                type="checkbox"
                                id={color}
                                checked={selectedColors.includes(color)} // Check if color is already selected
                                onChange={() => handleColorChange(color)}
                              />
                              <label
                                htmlFor={color}
                                className={
                                  activeColor === color ? 'color-active' : ''
                                }
                              >
                                {color}
                              </label>
                              <span
                                className={`it-shop-widget-checkbox-circle-self ${color
                                  .toLowerCase()
                                  .replace(' ', '_')}`}
                              ></span>
                            </div>
                            <span
                              className={
                                activeColor === color
                                  ? 'it-shop-widget-checkbox-circle-number color-active'
                                  : 'it-shop-widget-checkbox-circle-number'
                              }
                            >
                              {
                                displayProducts.filter(
                                  (product) => product.color === color
                                ).length
                              }
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="it-shop-widget mb-50">
                  <h3 className="it-shop-widget-title">Popular Brands</h3>
                  <div className="it-shop-widget-content ">
                    <div className="it-shop-widget-brand-list d-flex align-items-center justify-content-between flex-wrap">
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg3} alt="" />
                        </a>
                      </div>
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg1} alt="" />
                        </a>
                      </div>
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg2} alt="" />
                        </a>
                      </div>
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg4} alt="" />
                        </a>
                      </div>
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg6} alt="" />
                        </a>
                      </div>
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg5} alt="" />
                        </a>
                      </div>
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg8} alt="" />
                        </a>
                      </div>
                      <div className="it-shop-widget-brand-item">
                        <a href="#">
                          <img src={brandImg7} alt="" />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-xl-9 col-lg-8">
              <div className="shop-sidebar-left">
                <div className="it-shop-top pb-15">
                  <div className="row align-items-center">
                    <div className="col-xl-6 col-lg-6 col-md-6 mb-30">
                      <div className="it-shop-text">
                        {displayProducts.length !== 0 && (
                          <span>
                            Showing 1-{displayProducts.length} of{' '}
                            {displayProducts.length} products
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="col-xl-6 col-lg-6 col-md-6 mb-30">
                      <div className="it-shop-select-box text-md-end">
                        <div className="it-shop-select p-relative">
                          <select
                            className="custom-select"
                            onChange={handleSortCriteriaChange}
                            value={selectedSort}
                          >
                            <option value="default">Default sorting</option>
                            <option value="low-to-high">Low to High</option>
                            <option value="high-to-low">High to Low</option>
                            <option value="new-added">New Added</option>
                            <option value="on-sale">On Sale</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {displayProducts.length === 0 ? (
                  <div className="col-xl-9 col-lg-8">
                    <div className="it-error-content text-center pt-120 pb-100">
                      <h5
                        className="it-error-title wow animate__fadeInUp"
                        data-wow-duration=".9s"
                        data-wow-delay=".3s"
                      >
                        Sorry! Could not find the product you were looking
                        For!!!
                      </h5>
                      <p
                        className="mb-25 wow animate__fadeInUp"
                        data-wow-duration=".9s"
                        data-wow-delay=".5s"
                      >
                        Please check if you have misspelt something or try
                        searching with other words.
                      </p>
                      <Link
                        className="it-btn-theme mr-30 wow animate__fadeInUp"
                        data-wow-duration=".9s"
                        data-wow-delay=".7s"
                        to="#"
                        onClick={() => handleShopping()}
                      >
                        <Button btnText="Continue Shopping" />
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="row">
                    {displayProducts.map((product) => (
                      <div
                        key={product.id}
                        className="col-xl-4 col-lg-6 col-md-6 col-sm-6 mb-30"
                      >
                        <SingleShop
                          productId={product.id}
                          thumbnailImg={product.thumbnail}
                          badgeTitle={product.badgeTitle}
                          title={product.title}
                          price={product.price}
                          rating={product.rating}
                          category={product.category}
                          images={product.images}
                          inStock={product.inStock}
                          onAddToCart={() => handleAddToCart(product)}
                          onToggleWishlist={() => handleToggleWishlist(product)}
                          onToggleCompare={() => handleToggleCompare(product)}
                        />
                      </div>
                    ))}

                    <div className="row">
                      <div className="col-xl-12">
                        <div className="it-pagination mt-30">
                          <nav>
                            <ul>
                              <li>
                                <Link to="#">1</Link>
                              </li>
                              <li className="current">
                                <Link to="#">2</Link>
                              </li>
                              <li>
                                <Link to="#">3</Link>
                              </li>
                              <li>
                                <Link className="current" to="#">
                                  <i className="fa-solid fa-arrow-right-long"></i>
                                </Link>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ShopSidebarMain;
